<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div class="device-icon-pin" :class="sizeClass" :title="device.deviceName">
      <!-- The device's own glyph stays (P14 — the card's one allowed data
           fallback); the ring carries its first numeric reading whenever the
           row publishes the bounds for it, and keeps the bare track otherwise. -->
      <ControlStatusRing :value="pinRingValue">
        <GenericIcon :icon="device.icon || 'device_unknown'" :size="fluid ? 'lg' : (size ?? 'md')" :is-active="false" />
      </ControlStatusRing>
    </div>
  </template>

  <!-- ── Card mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'card'">
    <!-- Card face: whatever the device offers — its picture or glyph, its name
         and the first value the hub reports. -->
    <DeviceCardShell
      :size="cardSize ?? 'min'"
      :name="device.deviceName"
      :title="device.deviceName"
      accent="rgb(148, 163, 184)"
      class="generic-card-face"
      @click="emit('open-control-modal')"
      @contextmenu.prevent="emit('open-control-modal')">
      <template #media>
        <!-- Raw <img> rather than DeviceThumbnail: that component is a fixed 2rem
             circle for the compact tile, which left the card's photo tiny. -->
        <img v-if="thumbUrl" :src="thumbUrl" alt="" class="generic-card-face__img" draggable="false" />
        <span v-else class="material-symbols-outlined generic-card-face__glyph">{{ device.icon || 'widgets' }}</span>
      </template>

      <template v-if="cardSize !== 'min'" #value>
        <span class="generic-card-face__value">{{ firstValueStatus }}</span>
      </template>
    </DeviceCardShell>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || 'device_unknown'"
      :name="device.deviceName"
      :is-on="false"
      active-color="amber-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'control'">
    <div class="generic-sheet">
      <div v-if="!device.enabled" class="generic-empty">
        <ControlIcon name="power-off" size="1.75rem" />
        <p class="generic-empty-text">{{ tt('Device is disabled', 'Naprava je onemogočena') }}</p>
      </div>

      <template v-else-if="device.deviceValues && device.deviceValues.length > 0">
        <!-- One tile per value in the sheet language; loggable values keep
             their mini history chart under the tile. -->
        <div class="generic-grid">
          <div v-for="v in device.deviceValues" :key="v.id" class="generic-cell">
            <ControlValueTile
              :icon="valueIcon(v)"
              :label="v.name || v.valueKey"
              :value="formatValue(v.valueKey)"
              :unit="v.unit || ''" />
            <div v-if="v.loggingType && v.loggingType !== 'None'" class="generic-chart">
              <canvas :ref="(el: any) => setChartRef(v.id, el)" class="generic-canvas"></canvas>
            </div>
          </div>
        </div>
        <p v-if="lastUpdated" class="generic-meta">
          <ControlIcon name="clock" size="0.8125rem" />
          {{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}
        </p>
      </template>

      <div v-else class="generic-empty">
        <ControlIcon name="activity" size="1.75rem" />
        <p class="generic-empty-text">{{ tt('No device values available', 'Ni razpoložljivih vrednosti naprave') }}</p>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, onUnmounted, ref, nextTick } from 'vue';
import { formatTime } from '../../utils/formatDate';
import type { Chart as ChartInstance } from 'chart.js';
import GenericIcon from '../device-icons/GenericIcon.vue';
import DeviceCardShell from './DeviceCardShell.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import ControlValueTile from '../device-sheet/ControlValueTile.vue';
import { useDeviceValues } from '../../composables/useDeviceValues';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import { apiFetch } from '../../services/api';
import { getRuntimeConfig } from '../../config/runtime';
import { useWebRTCStore } from '../../stores/webrtc';
import { tt } from '../../utils/consoleText';
import { loadChartJs } from '../../utils/chartJs';
import type { Device, DeviceValueWithReferences } from '@/types';
import { readField, toFiniteNumber } from './deviceWidgetTypes';
import { getDefaultThumbnailUrl } from '../../composables/useDeviceThumbnail';
import type { ValueLogPoint } from './deviceWidgetTypes';

interface Props {
  device: Device;
  mode?: 'icon' | 'card' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  /** Set by the pages that grid cards (`DeviceButton`) — `undefined` keeps the
   *  compact button row other surfaces have always drawn. */
  cardSize?: 'min' | 'mid' | 'max';
}

const props = withDefaults(defineProps<Props>(), { mode: 'card', size: 'md', cardSize: undefined });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── State ────────────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

function formatValue(valueKey: string): string {
  let value: unknown;
  if (valueKey in currentValues.value) {
    value = currentValues.value[valueKey];
  } else {
    const dv = props.device.deviceValues?.find((v) => v.valueKey === valueKey);
    value = dv?.currentValue;
  }
  if (value === null || value === undefined) return '--';
  if (typeof value === 'number') return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (typeof value === 'object') return 'value' in value ? formatValue(value.value as string) : JSON.stringify(value);
  return String(value);
}

// ── Display ──────────────────────────────────────────────────────────────
/**
 * The ControlIcon a value's tile draws, folded from what the value says about
 * itself (key, name, unit) — never the device's own user-picked glyph, which
 * stays in the sheet head. The default is the pulse.
 */
const VALUE_ICON_RULES: Array<[RegExp, string]> = [
  [/(temp|°|celsius)/i, 'thermometer'],
  [/(humid|moist|water|level|rain)/i, 'drop'],
  [/(power|watt|energy|current|volt|ampere|batter|meter)/i, 'bolt'],
  [/(time|hour|duration|uptime|runtime)/i, 'clock'],
  [/(activ|state|status|enabled|online|switch|on_off)/i, 'power'],
];

function valueIcon(v: DeviceValueWithReferences): string {
  const hay = `${v.valueKey} ${v.name ?? ''} ${v.unit ?? ''}`;
  for (const [pattern, icon] of VALUE_ICON_RULES) {
    if (pattern.test(hay)) return icon;
  }
  return 'activity';
}

/** The card's picture, resolved through the S3-key blob cache (the one door
 *  the other card faces read `thumbnailUrl` through). */
const thumbUrl = computed(() => getDefaultThumbnailUrl(props.device));

const firstValueStatus = computed(() => {
  const first = props.device.deviceValues?.[0];
  if (!first) return props.device.deviceType || tt('Device', 'Naprava');
  const formatted = formatValue(first.valueKey);
  return first.unit ? `${formatted} ${first.unit}` : formatted;
});
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/**
 * The pin's own reading (P14): the first value row whose live number is finite —
 * the same bag the body reads, so the ring and the tiles cannot disagree.
 */
const pinReading = computed<{ value: number; row: DeviceValueWithReferences } | null>(() => {
  for (const row of props.device.deviceValues ?? []) {
    const raw = row.valueKey in currentValues.value ? currentValues.value[row.valueKey] : row.currentValue;
    const value = toFiniteNumber(raw);
    if (value !== null) return { value, row };
  }
  return null;
});

/**
 * The pin's ring (P14): that reading over the bounds its own row publishes
 * (`min`/`max`, either spelling). No bounds, or no numeric reading at all,
 * answers `null` — the ring keeps its bare track, because a generic device with
 * no range has no honest percentage to draw.
 */
const pinRingValue = computed<number | null>(() => {
  const entry = pinReading.value;
  if (!entry) return null;
  const min = toFiniteNumber(readField(entry.row, 'min') ?? readField(entry.row, 'min_value'));
  const max = toFiniteNumber(readField(entry.row, 'max') ?? readField(entry.row, 'max_value'));
  if (min === null || max === null || max <= min) return null;
  return ((entry.value - min) / (max - min)) * 100;
});

// ── Mini charts for control mode ─────────────────────────────────────────
const miniChartData = ref<Record<string, ValueLogPoint[]>>({});
const chartCanvasRefs: Record<string, HTMLCanvasElement | null> = {};
const chartInstances: Record<string, ChartInstance> = {};

function setChartRef(valueId: string, el: HTMLCanvasElement | null) {
  chartCanvasRefs[valueId] = el;
}

async function renderChartForValue(v: DeviceValueWithReferences) {
  const canvas = chartCanvasRefs[v.id];
  const data = miniChartData.value[v.id];
  if (!canvas || !data?.length) return;

  const { Chart } = await loadChartJs();

  // The sheet may have closed while the module loaded.
  if (chartCanvasRefs[v.id] !== canvas) return;

  if (chartInstances[v.id]) chartInstances[v.id].destroy();

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  let values: number[];
  if (v.loggingType === 'Analog') values = data.map((d) => toFiniteNumber(d.avg) ?? toFiniteNumber(d.value) ?? 0);
  else if (v.loggingType === 'Counter') values = data.map((d) => toFiniteNumber(d.delta) ?? 0);
  else values = data.map((d) => toFiniteNumber(d.value) ?? 0);

  chartInstances[v.id] = new Chart(ctx, {
    type: 'line',
    data: {
      labels: data.map((_, i: number) => i),
      datasets: [
        {
          data: values,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          borderWidth: 1.5,
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 0,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      scales: { x: { display: false }, y: { display: false } },
    },
  });
}

async function loadMiniChartData() {
  const loggable = props.device.deviceValues?.filter((v) => v.loggingType && v.loggingType !== 'None') || [];
  if (!loggable.length) return;

  const end = new Date().toISOString();
  const start = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

  await Promise.all(
    loggable.map((v) =>
      apiFetch(`/values/${v.id}/history?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`)
        .then((data) => {
          miniChartData.value[v.id] = data.data || [];
        })
        .catch(() => {
          miniChartData.value[v.id] = [];
        }),
    ),
  );
}

watch(
  miniChartData,
  async () => {
    await nextTick();
    const loggable = props.device.deviceValues?.filter((v) => v.loggingType && v.loggingType !== 'None') || [];
    loggable.forEach((v) => {
      void renderChartForValue(v);
    });
  },
  { deep: true },
);

onMounted(() => {
  if (props.mode === 'control') {
    // In cloud/WebRTC mode every history fetch travels over the capped data
    // channel.  Mounting many widgets simultaneously (e.g. opening a heavy room)
    // would saturate the channel and cause disconnects.  Skip the eager preload;
    // charts load on-demand when the user interacts with a widget instead.
    const cfg = getRuntimeConfig();
    const webrtcStore = useWebRTCStore();
    if (cfg.mode !== 'cloud' && !webrtcStore.connected) {
      loadMiniChartData();
    }
  }
});

onUnmounted(() => {
  Object.values(chartInstances).forEach((c) => c.destroy());
});
</script>

<style scoped>
/* Control mode: the value tiles in the sheet language. The host chrome owns
   the head and the panel; this block is the grid, the sparkline boxes and the
   meta line. */
.generic-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(0.875rem * var(--ui-space-factor));
}

.generic-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: calc(0.5rem * var(--ui-space-factor));
}

@media (min-width: 640px) {
  .generic-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.generic-cell {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
}

/* The mini history chart under its tile — the same 2.5rem box the old card
   gave the canvas. */
.generic-chart {
  height: 2.5rem;
  border-radius: calc(0.5rem * var(--ui-radius-factor));
  border: 1px solid var(--glass-edge);
  background: rgba(148, 163, 184, 0.1);
  overflow: hidden;
}

.dark .generic-chart {
  background: rgba(255, 255, 255, 0.04);
}

.generic-canvas {
  width: 100%;
  height: 100%;
}

.generic-meta {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375em;
  font-size: 0.75rem;
  color: rgb(var(--gray-500));
}

.dark .generic-meta {
  color: rgb(var(--gray-400));
}

/* The two empty states (disabled device / no values) keep the old copy. */
.generic-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: calc(2rem * var(--ui-space-factor)) 0;
  color: rgb(var(--gray-400));
}

.dark .generic-empty {
  color: rgb(var(--gray-500));
}

.generic-empty-text {
  margin: 0;
  font-size: 0.8125rem;
  color: rgb(var(--gray-500));
}

.dark .generic-empty-text {
  color: rgb(var(--gray-400));
}

/* ── Card face ───────────────────────────────────────────────────────────────
   The shell owns the frame, the name and the size rules; these are the device's
   own parts inside it. */
.generic-card-face__img {
  /* An ICON on this card, never a photo panel (owner, 25.10.2026: "should display
     images iconic", "not cover"). Contain so a cut-out is never cropped, and no
     inset of our own — the artwork fills the media box; the owner had us strip the
     padding we first added ("images are a bit too small.. remove padding"). */
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

.generic-card-face__glyph {
  font-size: 2.4rem;
}

.generic-card-face__value {
  flex: none;
  text-align: center;
  font-size: var(--card-value, 0.85rem);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #64748b;
}

.dark .generic-card-face__value {
  color: #94a3b8;
}
</style>
